import * as React from "react";
import { observer } from "mobx-react";
import { CampaignQueryResult, DealsProps } from "./typings";
import { withStores } from "stores";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { CampaignHeader } from "../../shared/Merchandising/Common/CampaignHeader/CampaignHeader";
import { useLocalization } from "@shared-ui/localization-context";
import { DealsPlaceholder } from "./DealsPlaceHolder";
import { GridPlaceholder } from "./GridPlaceHolder";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { AuthenticationState } from "bernie-context";
import { CampaignByIdQuery } from "../../../../__generated__/api";
import { MerchCampaignCard } from "components/shared/Merchandising/Common/MerchCampaignCard/MerchCampaignCard";
import { DealsErrorFallback } from "./DealsErrorFallback";
import { DealsDebugInfo } from "components/flexComponents/Deals/DealsDebugInfo";
import Campaigns = CampaignByIdQuery.Campaigns;
import Offers = CampaignByIdQuery.Offers;
import { locKeys } from "components/flexComponents/MerchCampaign/l10n";
import { UitkSecondaryButton } from "uitk-react-button";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLoader } from "uitk-react-loader";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";

const Deals = withStores(
  "routeStore",
  "dealsStore",
  "flexViewModel"
)(
  observer((props: DealsProps) => {
    const { flexComponent, flexViewModel } = props;
    const model = flexComponent.model;
    const flexContext = flexViewModel.flexContext;
    const { formatText } = useLocalization();
    const keyHelper = new ItemKeyHelper(flexComponent.id);
    const campaignId = model?.campaignId;
    const userContext = flexContext.userContext;
    const { lat, long, debug } = props.routeStore.queryParams;

    const [campaignQueryResult, setCampaignQueryResult] = React.useState<CampaignQueryResult>();
    const [campaign, setCampaign] = React.useState<Campaigns>();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [campaignFetchFailed, setCampaignFetchFailed] = React.useState<boolean>(false);
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [isLoadingNextPage, setIsLoadingNextPage] = React.useState<boolean>(false);

    const track = useClickTracker();
    const showMoreTrack = (id: string) => {
      const flexTracking: FlexTrackingInfo = {
        moduleName: id,
        rfrr: `${flexViewModel.trackingContext?.pageName}.offers.pagination`,
        action: Action.CLICK,
      };
      sendDelayedClickstreamTrackEvent(flexTracking, track);
    };

    if (!flexComponent.model) {
      return null;
    }

    const {
      destinationView,
      hotelInventoryView,
      displayDate,
      displayDescription,
      displayPrice,
      displayPropertyCount,
    } = flexComponent.model;

    React.useEffect(() => {
      fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
      if (shouldRender()) {
        setIsLoadingNextPage(true);
        props.dealsStore
          .fetchCampaignDeals(
            props.context,
            campaignId,
            model.enableTripAttach,
            pageNumber,
            model.campaignContentPagination ? model.campaignContentPageSize : null || undefined,
            lat ? lat : userContext.userLocation?.latitude,
            long ? long : userContext.userLocation?.longitude,
            model.displayPropertyCount,
            flexViewModel.trackingContext?.pageName
          )
          .then((result) => {
            if (campaign) {
              campaign.offers = campaign.offers?.concat(result.campaign?.offers || []);

              if (campaign.campaignContentPaginationInfo && result.campaign?.campaignContentPaginationInfo) {
                const hasMoreContentPages = result.campaign?.campaignContentPaginationInfo?.hasMoreContentPages;
                campaign.campaignContentPaginationInfo.hasMoreContentPages = hasMoreContentPages;
              }
              setCampaign(campaign);
            } else {
              setCampaign(result.campaign);
            }
            setCampaignQueryResult(result);
            setIsLoadingNextPage(false);
            setIsLoading(false);
            setCampaignFetchFailed(result === null);
            setPageNumber(pageNumber + 1);
          });
      }
    };

    const openDialog = (dialogPath: string) => {
      props.routeStore.updateSearch({ pwaDialog: dialogPath });
    };

    const closeDialog = () => {
      props.routeStore.closeDialog("pwaDialog");
    };

    const shouldRender = () => {
      return (
        campaignId &&
        (!model.enableTripAttach ||
          (model.enableTripAttach && userContext.userType === AuthenticationState.AUTHENTICATED))
      );
    };

    if (!shouldRender()) {
      return null;
    }

    const showErrorFallBack = () => {
      return model.isFeatured && (campaignFetchFailed || !hasEnoughDeals());
    };

    const hasEnoughDeals = () => {
      return campaign?.offers?.length && campaign?.offers?.length >= 4;
    };

    const renderMerchCampaignCard = (c: Campaigns, o: Offers, index: number) => {
      return (
        <MerchCampaignCard
          offer={o}
          cardIndex={index}
          campaignId={c.id}
          productTypes={c.productTypes}
          recommendedType={c.recommendedType}
          openDialog={openDialog}
          closeDialog={closeDialog}
          destinationView={destinationView}
          hotelInventoryView={hotelInventoryView}
          displayDate={displayDate}
          displayDescription={displayDescription}
          displayPrice={displayPrice}
          displayPropertyCount={displayPropertyCount}
          withBorder={true}
          moduleName="Deals"
        />
      );
    };

    const isGrid = model.view === "grid";

    return isLoading ? (
      isGrid ? (
        <GridPlaceholder flexComponentId={flexComponent.id} />
      ) : (
        <DealsPlaceholder flexComponentId={flexComponent.id} />
      )
    ) : (
      <>
        {debug === "true" && <DealsDebugInfo queryResult={campaignQueryResult} campaignId={campaignId} />}
        {showErrorFallBack() && <DealsErrorFallback />}

        {campaign && hasEnoughDeals() && (
          <div
            data-testid="deals"
            className="Deals uitk-cell all-b-margin-six"
            id={flexComponent.id}
            data-fm={flexComponent.fmId}
          >
            <div className="all-t-margin-six" data-testid="deals-carousel">
              <CampaignHeader
                campaign={campaign}
                openDialog={openDialog}
                closeDialog={closeDialog}
                campaignTitle={model.title}
                campaignCopyAlignment={model.campaignCopyAlignment}
                showCampaignDescription={model.showCampaignDescription}
                showCampaignTitle={model.showCampaignTitle}
                showCampaignTermsAndConditions={model.showTermsAndConditions}
                view={model.view}
                imgSrc={model.heroImageSrc}
              />
              {model.view === "carousel-vr" && (
                <Viewport>
                  <ViewSmall>
                    <IsomorphicCarousel
                      className="carouselComponent all-t-margin-three"
                      itemsVisible={{
                        lg: 2,
                        md: 2,
                        sm: 2,
                      }}
                      buttonText={{
                        nextButton: formatText("carousel.item.next"),
                        prevButton: formatText("carousel.item.prev"),
                      }}
                      pageBy={1}
                      peekWidth={0}
                    >
                      {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                        return (
                          <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
                            {renderMerchCampaignCard(campaign, offer, cardIndex)}
                          </div>
                        );
                      }) || []}
                    </IsomorphicCarousel>
                  </ViewSmall>
                  <ViewMedium>
                    <UitkLayoutGrid columns={4} space="three" className="all-x-padding-twelve">
                      {campaign.offers?.slice(0, 4).map((offer, cardIndex) => {
                        return (
                          <div className="uitk-height-item" key={keyHelper.next()} data-testid="deals-grid">
                            {renderMerchCampaignCard(campaign, offer, cardIndex)}
                          </div>
                        );
                      }) || []}
                    </UitkLayoutGrid>
                  </ViewMedium>
                </Viewport>
              )}
              {model.view === "carousel-redesign" && (
                <IsomorphicCarousel
                  className="carouselComponent all-t-margin-three"
                  itemsVisible={{
                    lg: 4,
                    md: 4,
                    sm: 2,
                  }}
                  buttonText={{
                    nextButton: formatText("carousel.item.next"),
                    prevButton: formatText("carousel.item.prev"),
                  }}
                  pageBy={1}
                  peekWidth={0}
                >
                  {campaign.offers?.map((offer, cardIndex) => {
                    return (
                      <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
                        {renderMerchCampaignCard(campaign, offer, cardIndex)}
                      </div>
                    );
                  }) || []}
                </IsomorphicCarousel>
              )}
              {(model.view === "carousel" || model.view == null) && (
                <IsomorphicCarousel
                  className="carouselComponent all-t-margin-three"
                  itemsVisible={{
                    lg: 3,
                    md: 2,
                    sm: 1,
                  }}
                  buttonText={{
                    nextButton: formatText("carousel.item.next"),
                    prevButton: formatText("carousel.item.prev"),
                  }}
                  pageBy={1}
                  peekWidth={24.8}
                >
                  {campaign.offers?.map((offer, cardIndex) => {
                    return (
                      <div className="uitk-height-item uitk-cell" key={keyHelper.next()}>
                        {renderMerchCampaignCard(campaign, offer, cardIndex)}
                      </div>
                    );
                  }) || []}
                </IsomorphicCarousel>
              )}
              {isGrid && (
                <UitkLayoutGrid
                  columns={{ medium: 2, extra_large: 3, small: 1 }}
                  space="three"
                  className="all-l-margin-three all-r-margin-three all-t-margin-three"
                >
                  {campaign.offers?.map((offer, cardIndex) => {
                    return (
                      <div className="uitk-height-item uitk-cell" key={keyHelper.next()} data-testid="deals-grid">
                        {renderMerchCampaignCard(campaign, offer, cardIndex)}
                      </div>
                    );
                  }) || []}
                </UitkLayoutGrid>
              )}
            </div>
            {model.campaignContentPagination && isGrid && campaign.campaignContentPaginationInfo?.hasMoreContentPages && (
              <div className="all-b-padding-six uitk-type-center all-t-padding-six">
                <UitkSecondaryButton
                  name="showMoreButton"
                  type="button"
                  onClick={fetchCampaigns}
                  text={formatText(locKeys.showMore)}
                  analytics={{
                    id: "merch.campaign",
                    callback: showMoreTrack,
                  }}
                />
              </div>
            )}
            {isLoadingNextPage && (
              <UitkLayoutFlex justifyContent="center">
                <UitkLayoutFlexItem className="all-t-margin-four">
                  <div>
                    <UitkLoader />
                  </div>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            )}
          </div>
        )}
      </>
    );
  })
);

export default Deals;
